/**
 * What a challenge is in each game: the number to beat, whether lower or
 * higher wins, and how to say it. A challenge is always for one mode of one
 * game (Snake on normal speed, Sudoku on hard, Color Sort level 12), so both
 * players face the same rules.
 *
 * Shared by the pages and the Worker (worker/challenges.ts), which checks
 * every challenge against these rules before storing it.
 */

import { GAME_SLUGS, type GameSlug } from '../game-slugs';

/** score: points, higher wins. time: milliseconds, lower wins. moves: lower wins. */
export type ChallengeMetric = 'score' | 'time' | 'moves';

/** One finished game, as a challenge describes it. */
export interface ChallengeRun {
  game: GameSlug;
  mode: string;
  value: number;
}

/** A stored challenge, as the API returns it. */
export interface Challenge extends ChallengeRun {
  id: string;
  metric: ChallengeMetric;
  createdAt: string;
}

export interface ModeRule {
  /** How the mode reads on its own: "Normal speed", "Hard", "Level 12". */
  label: string;
  metric: ChallengeMetric;
  /** Times shown to hundredths (Tetris sprint), not whole seconds. */
  precise?: boolean;
}

type Rules = (mode: string) => ModeRule | null;

function has(record: Record<string, string>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function fixed(metric: ChallengeMetric, labels: Record<string, string>, precise = false): Rules {
  return (mode) => (has(labels, mode) ? { label: labels[mode], metric, precise } : null);
}

/** "level-12" style modes, for games whose levels are the same for everyone. */
function numberedLevels(metric: ChallengeMetric, prefix: string, max: number, label: (n: number) => string): Rules {
  const pattern = new RegExp(`^${prefix}-(\\d{1,4})$`);
  return (mode) => {
    const n = Number(pattern.exec(mode)?.[1]);
    return Number.isInteger(n) && n >= 1 && n <= max ? { label: label(n), metric } : null;
  };
}

const MAZE_DIFFICULTIES: Record<string, string> = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };
const MAZE_LEVELS = 20;

const RULES: Record<GameSlug, Rules> = {
  snake: fixed('score', { slow: 'Slow speed', normal: 'Normal speed', fast: 'Fast speed' }),
  tetris: (mode) =>
    mode === 'marathon'
      ? { label: 'Marathon', metric: 'score' }
      : mode === 'sprint'
        ? { label: 'Sprint, 40 lines', metric: 'time', precise: true }
        : null,
  '2048': fixed('score', { classic: 'Classic' }),
  blockdestroyer: fixed('score', { easy: 'Easy', normal: 'Normal', hard: 'Hard' }),
  'match-3': fixed('score', { endless: 'Endless', timed: 'Timed, 2 minutes', moves: '30 moves' }),
  'quick-math': fixed('score', { 'grade-1': 'Grade 1', 'grade-2': 'Grade 2', 'grade-3': 'Grade 3', 'grade-4': 'Grade 4', 'grade-5': 'Grade 5' }),
  'pattern-challenge': fixed('score', { 'level-1': 'From level 1', 'level-2': 'From level 2', 'level-3': 'From level 3', 'level-4': 'From level 4' }),
  'forest-runner': fixed('score', { relaxed: 'Relaxed pace', normal: 'Normal pace', fast: 'Fast pace' }),
  'endless-shred': fixed('score', { green: 'Green run', blue: 'Blue run', red: 'Red run', black: 'Black diamond', double: 'Double black diamond' }),
  solitaire: fixed('time', { draw1: 'Draw 1', draw3: 'Draw 3' }),
  sudoku: fixed('time', { easy: 'Easy', medium: 'Medium', hard: 'Hard' }),
  minesweeper: fixed('time', { beginner: 'Beginner', intermediate: 'Intermediate', expert: 'Expert' }),
  'memory-game': fixed('moves', { easy: 'Easy', medium: 'Medium', hard: 'Hard' }),
  'sort-puzzle': numberedLevels('moves', 'level', 9999, (n) => `Level ${n}`),
  maze: (mode) => {
    const [difficulty, level] = mode.split('-');
    const n = Number(level);
    if (!has(MAZE_DIFFICULTIES, difficulty) || !Number.isInteger(n) || n < 1 || n > MAZE_LEVELS) return null;
    return { label: `${MAZE_DIFFICULTIES[difficulty]} level ${n}`, metric: 'time' };
  },
};

/** Sane limits, so a hand-made request cannot store an absurd number. */
const BOUNDS: Record<ChallengeMetric, { min: number; max: number }> = {
  score: { min: 1, max: 100_000_000 },
  time: { min: 1_000, max: 24 * 60 * 60 * 1000 },
  moves: { min: 1, max: 1_000_000 },
};

export function isGameSlug(value: unknown): value is GameSlug {
  return typeof value === 'string' && GAME_SLUGS.some((slug) => slug === value);
}

export function ruleFor(game: GameSlug, mode: string): ModeRule | null {
  return RULES[game](mode);
}

/** True for a run that can be stored as a challenge. */
export function isValidRun(run: ChallengeRun): boolean {
  const rule = ruleFor(run.game, run.mode);
  if (!rule) return false;
  const { min, max } = BOUNDS[rule.metric];
  return Number.isInteger(run.value) && run.value >= min && run.value <= max;
}

export function formatValue(metric: ChallengeMetric, value: number, precise = false): string {
  if (metric !== 'time') return value.toLocaleString('en-US');
  const totalSeconds = Math.floor(value / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  const fraction = precise ? `.${String(Math.floor((value % 1000) / 10)).padStart(2, '0')}` : '';
  return minutes >= 60
    ? `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}:${seconds}${fraction}`
    : `${minutes}:${seconds}${fraction}`;
}

/** The run's number as it reads on cards and banners: "1,240", "3:42", "24 moves". */
export function describeValue(run: ChallengeRun): string {
  const rule = ruleFor(run.game, run.mode);
  if (!rule) return String(run.value);
  const value = formatValue(rule.metric, run.value, rule.precise);
  return rule.metric === 'moves' ? `${value} ${run.value === 1 ? 'move' : 'moves'}` : value;
}

/** The label above the number on a card. */
export function metricLabel(metric: ChallengeMetric): string {
  return metric === 'score' ? 'Score' : metric === 'time' ? 'Time' : 'Moves';
}

export type Outcome = 'won' | 'lost' | 'tied';

/** How `run` did against `challenge`. Both must be the same game and mode. */
export function compare(run: ChallengeRun, challenge: ChallengeRun): Outcome {
  const rule = ruleFor(challenge.game, challenge.mode);
  if (run.value === challenge.value) return 'tied';
  const lowerWins = rule !== null && rule.metric !== 'score';
  return (lowerWins ? run.value < challenge.value : run.value > challenge.value) ? 'won' : 'lost';
}

/** The gap between two values, in the metric's own terms: "140 points", "0:12", "3 moves". */
export function describeGap(challenge: ChallengeRun, other: number): string {
  const rule = ruleFor(challenge.game, challenge.mode);
  const gap = Math.abs(other - challenge.value);
  if (!rule || rule.metric === 'score') return `${gap.toLocaleString('en-US')} ${gap === 1 ? 'point' : 'points'}`;
  if (rule.metric === 'moves') return `${gap.toLocaleString('en-US')} ${gap === 1 ? 'move' : 'moves'}`;
  return formatValue('time', gap, rule.precise);
}

/** "Beat 1,240 in Snake", for banners, link previews and share text. */
export function challengeHeadline(run: ChallengeRun, gameName: string): string {
  return `Beat ${describeValue(run)} in ${gameName}`;
}

/** One sentence for link previews: what was done, on which mode, and the dare. */
export function challengeSummary(run: ChallengeRun, gameName: string): string {
  const rule = ruleFor(run.game, run.mode);
  const value = describeValue(run);
  const mode = rule ? ` (${rule.label})` : '';
  if (!rule || rule.metric === 'score') return `A friend scored ${value} in ${gameName}${mode}. Can you beat it?`;
  if (rule.metric === 'time') return `A friend finished ${gameName}${mode} in ${value}. Can you do it faster?`;
  return `A friend finished ${gameName}${mode} in ${value}. Can you do it in fewer?`;
}

/** A challenge as the API returns it, checked against the rules; null otherwise. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function toChallenge(value: unknown): Challenge | null {
  if (!isRecord(value)) return null;
  const { id, game, mode, metric, value: score, createdAt } = value;
  if (typeof id !== 'string' || !isGameSlug(game) || typeof mode !== 'string' || typeof score !== 'number') return null;
  if (typeof createdAt !== 'string') return null;
  const rule = ruleFor(game, mode);
  if (!rule || rule.metric !== metric) return null;
  return { id, game, mode, metric: rule.metric, value: score, createdAt };
}
